import type { ColorScheme } from '@keboola/design';

/**
 * Where the reader's light/dark choice lives.
 *
 * ⚠️ **Also hardcoded in `public/colorScheme.js`.** The pre-paint script there reads this key before
 * React mounts, so the two must not drift — a rename here alone brings back the light flash it exists
 * to prevent. That script cannot import from this module: it runs before any bundle has loaded.
 */
export const COLOR_SCHEME_STORAGE_KEY = 'keboola-developer-portal/color-scheme';

// `satisfies` rather than an annotation: it still rejects a value that is not a `ColorScheme`, while
// leaving the tuple literal narrow enough that `some` compares against `unknown` without an assertion.
const SCHEMES = ['light', 'dark', 'system'] as const satisfies readonly ColorScheme[];

const isColorScheme = (value: unknown): value is ColorScheme =>
  SCHEMES.some((scheme) => scheme === value);

/**
 * The reader's stored choice, or `system` when there isn't one.
 *
 * Anything unexpected — absent, or a value written by something else in this origin — resolves to
 * `system` rather than escaping as `undefined`: that value reaches `ThemeProvider` as a *controlled*
 * prop, and `undefined` there silently switches the provider to uncontrolled.
 */
export const readPersistedScheme = (): ColorScheme => {
  try {
    const raw = globalThis.localStorage?.getItem(COLOR_SCHEME_STORAGE_KEY);
    return isColorScheme(raw) ? raw : 'system';
  } catch (error) {
    // `localStorage` throws outright in a partitioned or cookie-blocked context, not just when empty.
    console.warn('Could not read the stored colour scheme.', error);
    return 'system';
  }
};

/** Stores the choice. A failure is logged, never thrown — the scheme still applies to this tab. */
export const persistScheme = (scheme: ColorScheme): void => {
  try {
    globalThis.localStorage?.setItem(COLOR_SCHEME_STORAGE_KEY, scheme);
  } catch (error) {
    console.warn('Could not store the colour scheme.', error);
  }
};
